import type { Token, Tokens } from "marked";
import { createElement, FC, ReactNode } from "react";
import { MentionToken } from "./mention";
import { EmojiToken } from "./emoji";

import * as styles from "../styles.module.scss";
import clsx from "clsx";

export interface RendererProps {
  token: Token;
  children: ReactNode;
  renderers: { [key: string]: FC<RendererProps> };
}

// Checkbox, HTML, Table, TableRow, TableCell, Image are not supported by Discord's markdown.
export const DefaultRenderers = {
  blockquote: Blockquote,
  br: Break,
  code: Code,
  codespan: CodeSpan,
  del: Del,
  em: Emphasis,
  emoji: Emoji,
  heading: Heading,
  hr: HorizonalRule,
  link: Link,
  list: List,
  listitem: ListItem,
  mention: Mention,
  paragraph: Paragraph,
  space: Space,
  strong: Strong,
  text: Text,
}

function Blockquote({ children }: RendererProps) {
  return (
    <div className={styles.blockquoteContainer}>
      <div />
      <blockquote>
        <span>{children}</span>
      </blockquote>
    </div>
  );
}

function Break() {
  return <br />;
}

function Code({ children, token }: RendererProps) {
  let lang = (token as Tokens.Code).lang;
  return <code lang={lang}>{children}</code>;
}

function CodeSpan({ children }: RendererProps) {
  return <code className={styles.inline}>{children}</code>;
}

function Del({ children }: RendererProps) {
  return <del>{children}</del>;
}

function Emphasis({ children }: RendererProps) {
  return <em>{children}</em>;
}

function Emoji({ token }: RendererProps) {
  return (
    <img
      title={(token as EmojiToken).name}
      src={`https://cdn.discordapp.com/emojis/${(token as EmojiToken).id}`}
      className={clsx(styles.emoji, (token as EmojiToken).inline ? styles.inlineEmoji : undefined)}
    />
  );
}

function Heading({ children, token }: RendererProps) {
  let depth = (token as Tokens.Heading).depth;
  return createElement(`h${depth}`, null, children);
}

function HorizonalRule() {
  return <hr />;
}

function Link({ children, token }: RendererProps) {
  let href = (token as Tokens.Link).href;
  return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
}

function List({ children, token }: RendererProps) {
  token = (token as Tokens.List);

  if (token.ordered) {
    return <ol>{children}</ol>;
  }
  return <ul>{children}</ul>
}

function ListItem(props: RendererProps) {
  const { children, token } = props
  return <li>{children}</li>;
}

function Mention({ token }: RendererProps) {
  // todo maybe
  let mentionType = (token as MentionToken).mentionType;
  const mentions = { user: "@unknown-user", channel: "#unknown", role: "@unknown-role" };

  return (
    <span className={styles.mention}>
      {mentions[mentionType]}
    </span>
  )
}

function Paragraph({ children }: RendererProps) {
  return <p>{children}</p>;
}

function Space({ token: { raw } }: RendererProps) {
  if (raw == "\n") {
    return <br />
  };
}

function Strong({ children }: RendererProps) {
  return <strong>{children}</strong>
}

function Text(props: RendererProps) {
  return props.children;
}
